// Reserve-cap math shared by the supply and borrow forms (and multiply). A form's "max" is never
// just the user's wallet or collateral - it is also bounded by the RESERVE: how much room is left
// under the supply/borrow cap, how much idle liquidity there is, and whether borrowing is disabled.
// Ignoring these lets the UI offer an action the pool then reverts (SupplyCapExceeded /
// BorrowCapExceeded / BorrowingDisabled / insufficient liquidity). Pure bigint math, unit-tested.

// Reserve caps use type(uint128).max as the "uncapped" sentinel (matches the contract +
// useProtocolState); a cap equal to this means "no limit", distinct from an actual 0 (closed).
export const UNCAPPED_CAP = 2n ** 128n - 1n
const MAX_UINT256 = 2n ** 256n - 1n

/// Remaining headroom under a reserve cap: 0 when the cap is full (or an explicit 0/closed),
/// unbounded (MAX) when the cap is the uncapped sentinel.
export function capHeadroomWei(capWei, filledWei) {
  if (capWei === UNCAPPED_CAP) return MAX_UINT256
  return capWei > filledWei ? capWei - filledWei : 0n
}

/// Smallest of several bigint ceilings.
export function minWei(...vals) {
  return vals.reduce((m, v) => (v < m ? v : m))
}

/// The most a user can actually BORROW of a reserve: 0 when borrowing is disabled, otherwise the
/// tightest of their collateral-driven ceiling, the borrow-cap headroom, and the idle liquidity.
export function effectiveBorrowMaxWei({ collateralMaxWei, borrowCapWei, totalBorrowsWei, availableLiquidityWei, borrowingDisabled }) {
  if (borrowingDisabled) return 0n
  return minWei(collateralMaxWei, capHeadroomWei(borrowCapWei, totalBorrowsWei), availableLiquidityWei)
}

/// The most a user can actually SUPPLY: the tighter of their wallet balance and the supply-cap headroom.
export function effectiveSupplyMaxWei({ walletMaxWei, supplyCapWei, totalSuppliedWei }) {
  return minWei(walletMaxWei, capHeadroomWei(supplyCapWei, totalSuppliedWei))
}
